// Changes (audit) page (SPEC §7.6): newest first, 50 per page, filter by dates and record type.

import type { ApiResult } from "../../api/client";
import { fetchChanges } from "../../api/views";
import type { ChangeLogEntry, ChangesPage } from "../../api/types";
import { addDays } from "../../dates";
import { formatTimestamp, t, translateCode } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { requireSchool } from "../../session";
import { createButton, createElement, createLink } from "../../ui/dom";
import { createInputField, createSelectField, type Field } from "../../ui/forms";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { buildPager } from "../../ui/pager";

const ENTITY_TYPES: string[] = ["Lesson", "Group", "Teacher", "Classroom", "Center", "Course", "User", "TravelTimes"];

interface ChangesFilterState {
  from: string;
  to: string;
  entityType: string;
  page: number;
}

export async function renderChangesPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const today: string = requireSchool().today;
  const filter: ChangesFilterState = {
    from: route.query.get("from") ?? addDays(today, -30),
    to: route.query.get("to") ?? today,
    entityType: route.query.get("type") ?? "",
    page: Number(route.query.get("page") ?? "1"),
  };
  const page: HTMLElement = startPage(container, t("changes.title"));
  page.append(createLoading());
  const result: ApiResult<ChangesPage> = await fetchChanges({ ...filter, entityType: filter.entityType || null });
  if (result.ok === false) {
    showPageProblem(page, result.problem);
    return;
  }

  const draw = (): void => {
    drawPage(startPage(container, t("changes.title")), filter, result.data);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(page: HTMLElement, filter: ChangesFilterState, data: ChangesPage): void {
  page.append(buildFilter(filter));
  if (data.entries.length === 0) {
    page.append(createElement("p", "muted", t("changes.empty")));
    return;
  }

  const wrapper: HTMLDivElement = createElement("div", "table-scroll");
  wrapper.append(buildTable(data.entries));
  page.append(wrapper, buildChangesPager(filter, data));
}

function buildFilter(filter: ChangesFilterState): HTMLElement {
  const from: Field<HTMLInputElement> = createInputField(t("app.from"), "date", filter.from);
  const to: Field<HTMLInputElement> = createInputField(t("app.to"), "date", filter.to);
  const options: { value: string; label: string }[] = [{ value: "", label: t("changes.allTypes") }, ...ENTITY_TYPES
    .map((type: string): { value: string; label: string } => ({ value: type, label: translateCode("entityTypes",
      type) }))];
  const type: Field<HTMLSelectElement> = createSelectField(t("changes.entityType"), options, filter.entityType);
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(from.wrapper, to.wrapper, type.wrapper, createButton(t("app.search"), (): void => {
    navigate(buildPath({ from: from.input.value, to: to.input.value, entityType: type.input.value, page: 1 }));
  }));
  return toolbar;
}

function buildTable(entries: ChangeLogEntry[]): HTMLTableElement {
  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("changes.when"), t("changes.who"), t("changes.what"), t("changes.record"),
    t("changes.details")]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const entry of entries) {
    body.append(buildRow(entry));
  }

  return table;
}

function buildRow(entry: ChangeLogEntry): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  row.append(createElement("td", "", formatTimestamp(entry.at, requireSchool().timeZone)),
    createElement("td", "", entry.userName), createElement("td", "", translateCode("actions", entry.action)));
  const record: HTMLTableCellElement = createElement("td");
  record.append(buildRecordLink(entry));
  row.append(record, createElement("td", "muted", describeDetails(entry.details)));
  return row;
}

/** A link to the affected lesson, teacher, group, classroom or center (SPEC §7.6). */
function buildRecordLink(entry: ChangeLogEntry): HTMLElement {
  const label: string = translateCode("entityTypes", entry.entityType);
  if (entry.lessonKey !== null) {
    const key = entry.lessonKey;
    return createLink(`/lessons/${key.groupId}/${key.date}/${key.slotId}`, label);
  }

  const routes: Record<string, string> = {
    Group: "/groups/", Teacher: "/teachers/", Classroom: "/admin/classrooms/", Center: "/admin/centers/",
  };
  const prefix: string | undefined = routes[entry.entityType];
  return prefix === undefined ? createElement("span", "", label) : createLink(prefix + entry.entityId, label);
}

/** Compact "name: value" text of the details (values are data entered by users, shown as they are). */
function describeDetails(details: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [name, value] of Object.entries(details)) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      parts.push(`${name}: ${String(value)}`);
    }
  }

  return parts.join(", ");
}

function buildChangesPager(filter: ChangesFilterState, data: ChangesPage): HTMLElement {
  const pages: number = Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  return buildPager(data.page, pages, (page: number): void => {
    navigate(buildPath({ ...filter, page }));
  });
}

function buildPath(filter: ChangesFilterState): string {
  const query: URLSearchParams = new URLSearchParams({ from: filter.from, to: filter.to, page: String(filter.page) });
  if (filter.entityType !== "") {
    query.set("type", filter.entityType);
  }

  return `/changes?${query.toString()}`;
}
